import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Row,
  Select,
  Slider,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import useCollections from "../hooks/useCollections";
import useEventsLastChange from "../hooks/useEventsLastChange";
import useEventsV2 from "../hooks/useEventsV2";
import useFlowDetectionsLastRecord from "../hooks/useFlowDetectionsLastRecord";
import useSellersData from "../hooks/useSellersData";
import useStoresData from "../hooks/useStoresData";
import useZones from "../hooks/useZones";
import {
  JANELA_VIGENCIA,
  PERIODO_PADRAO,
  periodoSemiaberto,
} from "../pages/collections-change/promoHelpers";
import { opcoesDeEventos } from "../pages/revenue/eventoHelpers";

const { Text } = Typography;
const DIAS_DA_JANELA = [PERIODO_PADRAO, 15, 30, 60, 90];
const OPCOES_DO_DEPOIS = DIAS_DA_JANELA.map((dias) => ({
  value: dias,
  label: `os primeiros ${dias} dias`,
}));
const OPCOES_DO_ANTES = [
  { value: null, label: "mesma duração, antes" },
  ...DIAS_DA_JANELA.map((dias) => ({
    value: dias,
    label: `os ${dias} dias anteriores`,
  })),
];
const OPCAO_DA_VIGENCIA = {
  value: JANELA_VIGENCIA,
  label: "a vigência inteira",
};
const OPCOES_COM_VIGENCIA = [OPCAO_DA_VIGENCIA, ...OPCOES_DO_DEPOIS];
const AVISOS_DA_JANELA = {
  generico:
    "Eventos com data de fim usam a vigência inteira no lugar do primeiro campo.",
  comFim:
    "Esta promoção tem data de fim: compare a vigência inteira ou só os primeiros dias dela.",
  semFim:
    "Esta promoção não tem data de fim, então você escolhe os dois lados.",
};
const DEFAULT_PERIOD_MIN = 9;
const DEFAULT_PERIOD_MAX = 19;
const DEFAULT_FILTER_TITLE = null;

const clampPeriod = (period, minHour, maxHour) => {
  if (!Array.isArray(period) || period.length !== 2) {
    return [minHour, maxHour];
  }

  let [start, end] = period.map((value) => Number(value));
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return [minHour, maxHour];
  }

  start = Math.max(minHour, Math.min(start, maxHour - 1));
  end = Math.max(start + 1, Math.min(end, maxHour));

  return [start, end];
};

const getYesterdayDateRange = () => {
  const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
  return [yesterday, yesterday];
};

const getDefaultLastThirtyDaysRangeExcludingToday = () => {
  const endDate = dayjs().subtract(1, "day").format("YYYY-MM-DD");
  const startDate = dayjs().subtract(30, "day").format("YYYY-MM-DD");
  return [startDate, endDate];
};

const getDefaultDateRangeByPage = (page) => {
  if (page === "flow_detections") {
    return getYesterdayDateRange();
  }

  if (
    page === "geral" ||
    page === "home" ||
    page === "revenue" ||
    page === "collections_change" ||
    page === "peak_hours"
  ) {
    return getDefaultLastThirtyDaysRangeExcludingToday();
  }

  return getYesterdayDateRange();
};

const getRealItems = (items) =>
  items?.filter((item) => item.value !== "all") ?? [];

const getSelectValue = (localValue, realItems) =>
  localValue.includes("all") ? realItems.map((i) => i.value) : localValue;

const makeOnChange = (setter, realItems) => (values) => {
  if (values.length === 0 || values.length === realItems.length) {
    setter(["all"]);
  } else {
    setter(values);
  }
};

// eslint-disable-next-line react/display-name
const makeDropdownRender = (localValue, setter, realItems) => (menu) => {
  const allSelected = localValue.includes("all");
  const indeterminate = !allSelected && localValue.length > 0;
  return (
    <div>
      <div
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          if (allSelected) setter([]);
          else setter(["all"]);
        }}
        style={{
          padding: "5px 12px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          borderBottom: "1px solid #f0f0f0",
        }}>
        <Checkbox
          indeterminate={indeterminate}
          checked={allSelected}
          onClick={(e) => e.stopPropagation()}
        />
        <span>Selecionar todos</span>
        {indeterminate && (
          <span style={{ marginLeft: "auto", color: "#999", fontSize: 11 }}>
            {localValue.length} / {realItems.length}
          </span>
        )}
      </div>
      {menu}
    </div>
  );
};

// eslint-disable-next-line react/display-name
const makeOptionRender = (localValue) => (option) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <Checkbox
      checked={localValue.includes("all") || localValue.includes(option.value)}
      onClick={(e) => e.stopPropagation()}
    />
    <span>{option.label}</span>
  </div>
);

const selectSearchProps = {
  showSearch: true,
  optionFilterProp: "label",
  filterOption: (input, option) =>
    String(option?.label || "")
      .toLowerCase()
      .includes(String(input || "").toLowerCase()),
};

export default function Filters({
  isMainDataLoading = false,
  filters = ["data", "periodo", "zonas", "colecoes", "lojas"],
  title = DEFAULT_FILTER_TITLE,
  cardStyle,
  compact = true,
  selectedDate,
  setSelectedDate,
  selectedPeriod,
  setSelectedPeriod,
  selectedZones,
  setSelectedZones,
  selectedCollections,
  setSelectedCollections,
  selectedStores,
  setSelectedStores,
  selectedSellers,
  setSelectedSellers,
  selectedEvent = null,
  setSelectedEvent,
  selectedAnalysisDays = 7,
  setSelectedAnalysisDays,
  selectedBaseline = null,
  setSelectedBaseline,
  dateRange = false,
  selectedDateRange,
  setSelectedDateRange,
  page = "flow_detections",
  periodMin = DEFAULT_PERIOD_MIN,
  periodMax = DEFAULT_PERIOD_MAX,
  extraControls = null,
  eventoComVigencia = null,
  dateAddon = null,
  collectionsOptions = null,
}) {
  const [localDate, setLocalDate] = useState(selectedDate);
  const [localDateRange, setLocalDateRange] = useState(selectedDateRange);
  const [localPeriod, setLocalPeriod] = useState(selectedPeriod);
  const [localZones, setLocalZones] = useState(selectedZones);
  const [localCollections, setLocalCollections] = useState(selectedCollections);
  const [localStores, setLocalStores] = useState(selectedStores);
  const [localSellers, setLocalSellers] = useState(selectedSellers);
  const [localEvent, setLocalEvent] = useState(selectedEvent);
  const [localAnalysisDays, setLocalAnalysisDays] =
    useState(selectedAnalysisDays);
  const [localBaseline, setLocalBaseline] = useState(selectedBaseline);
  const [pickingStart, setPickingStart] = useState(null);
  const comVigencia = eventoComVigencia === true;
  const opcoesDoDepois = comVigencia ? OPCOES_COM_VIGENCIA : OPCOES_DO_DEPOIS;
  const opcaoDoDepois = opcoesDoDepois.some(
    (opcao) => opcao.value === localAnalysisDays
  )
    ? localAnalysisDays
    : opcoesDoDepois[0].value;
  const avisoDaJanela =
    eventoComVigencia === null
      ? AVISOS_DA_JANELA.generico
      : comVigencia
        ? AVISOS_DA_JANELA.comFim
        : AVISOS_DA_JANELA.semFim;
  const [pickerDates, setPickerDates] = useState(null);

  const useDayRangePicker =
    page === "geral" ||
    page === "home" ||
    page === "revenue" ||
    page === "flow_detections" ||
    page === "collections_change" ||
    page === "peak_hours";

  const maxFlowRangeSpanDays = 7;
  const compactControlSize = compact ? "small" : "middle";
  const compactCardBodyPadding = compact ? "12px 14px 16px" : "24px";
  const compactRowMinHeight = compact ? "64px" : "75px";
  const compactGutter = compact ? [12, 12] : [16, 16];
  const datePickerWidth = compact ? "230px" : "250px";
  const selectWidth = compact ? "190px" : "200px";
  const filterColStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  };
  const labelStyle = {
    marginBottom: 6,
    lineHeight: 1.2,
  };
  const normalizedPeriodMin = Number.isFinite(periodMin)
    ? periodMin
    : DEFAULT_PERIOD_MIN;
  const normalizedPeriodMax = Number.isFinite(periodMax)
    ? periodMax
    : DEFAULT_PERIOD_MAX;
  const safePeriodMax =
    normalizedPeriodMax > normalizedPeriodMin
      ? normalizedPeriodMax
      : normalizedPeriodMin + 1;

  const { data: lastRecord, isPending: isLastRecordPending } =
    useFlowDetectionsLastRecord({ enabled: page === "flow_detections" });
  const { data: zones, isLoading: isZonesLoading } = useZones({
    active_only: true,
  });
  const { data: lastChange, isPending: isLastChangePending } =
    useEventsLastChange({ enabled: page === "collections_change" });
  const { data: collectionsFromDb, isLoading: isCollectionsLoading } =
    useCollections();
  const collections = collectionsOptions ?? collectionsFromDb;
  const { data: stores, isLoading: isStoresLoading } = useStoresData({
    enabled: page === "revenue",
  });
  const { data: sellers, isLoading: isSellersLoading } = useSellersData({
    enabled: page === "revenue" && filters.includes("vendedores"),
    start_date: localDateRange?.[0],
    end_date: localDateRange?.[1],
  });

  useEffect(() => {
    setLocalSellers(["all"]);
  }, [localDateRange?.[0], localDateRange?.[1]]);

  const eventosHabilitados = page === "revenue" && filters.includes("eventos");
  const periodoDosEventos = useMemo(
    () => periodoSemiaberto(localDateRange),
    [localDateRange?.[0], localDateRange?.[1]]
  );
  const { data: eventosV2, isFetching: isEventsFetching } = useEventsV2({
    start: periodoDosEventos?.start,
    end: periodoDosEventos?.end,
    enabled: eventosHabilitados,
  });
  const eventOptions = useMemo(
    () => opcoesDeEventos(eventosV2?.events),
    [eventosV2]
  );
  const eventoAtivo = eventosHabilitados && localEvent !== null;

  useEffect(() => {
    if (!eventosV2 || localEvent === null) return;
    if (!eventOptions.some((opcao) => opcao.value === localEvent)) {
      setLocalEvent(null);
    }
  }, [eventosV2, eventOptions, localEvent]);

  const handleEventChange = (valor) => {
    const evento = valor ?? null;
    setLocalEvent(evento);
    if (evento !== null) {
      setLocalZones(["all"]);
      setLocalCollections(["all"]);
      setLocalSellers(["all"]);
    }
  };

  const loading =
    (isLastRecordPending && isZonesLoading) ||
    isCollectionsLoading ||
    isLastChangePending ||
    isStoresLoading ||
    isSellersLoading;

  useEffect(() => {
    setLocalDate(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    setLocalDateRange(selectedDateRange);
  }, [selectedDateRange]);

  useEffect(() => {
    setLocalZones(selectedZones);
  }, [selectedZones]);

  useEffect(() => {
    setLocalCollections(selectedCollections);
  }, [selectedCollections]);

  useEffect(() => {
    setLocalStores(selectedStores);
  }, [selectedStores]);

  useEffect(() => {
    setLocalSellers(selectedSellers);
  }, [selectedSellers]);

  useEffect(() => {
    setLocalEvent(selectedEvent);
  }, [selectedEvent]);

  useEffect(() => {
    setLocalAnalysisDays(selectedAnalysisDays);
  }, [selectedAnalysisDays]);

  useEffect(() => {
    setLocalBaseline(selectedBaseline);
  }, [selectedBaseline]);

  useEffect(() => {
    if (Array.isArray(selectedPeriod) && selectedPeriod.length === 2) {
      setLocalPeriod(
        clampPeriod(selectedPeriod, normalizedPeriodMin, safePeriodMax)
      );
      return;
    }

    setLocalPeriod(selectedPeriod);
  }, [selectedPeriod, normalizedPeriodMin, safePeriodMax]);

  useEffect(() => {
    if (dateRange && useDayRangePicker) {
      const hasLocalRange =
        Array.isArray(localDateRange) &&
        Boolean(localDateRange?.[0]) &&
        Boolean(localDateRange?.[1]);

      if (!hasLocalRange) {
        const defaultRange = getDefaultDateRangeByPage(page);
        setLocalDateRange(defaultRange);
        setSelectedDateRange?.(defaultRange);
      }
      return;
    }

    if (!lastChange && !lastRecord) return;

    if (page === "collections_change") {
      if (!isLastChangePending && lastChange?.date) {
        const formattedDate = dayjs(lastChange.date).format(
          useDayRangePicker ? "YYYY-MM-DD" : "YYYY-MM"
        );

        if (dateRange) {
          setLocalDateRange([formattedDate, formattedDate]);
          setSelectedDateRange?.([formattedDate, formattedDate]);
        } else {
          setLocalDate(formattedDate);
          setSelectedDate?.(formattedDate);
        }
      }
    } else if (page !== "revenue" && page !== "geral") {
      if (!isLastRecordPending && lastRecord?.date) {
        const formattedDate = dayjs(lastRecord.date).format("YYYY-MM-DD");

        if (dateRange) {
          if (useDayRangePicker) {
            setLocalDateRange([formattedDate, formattedDate]);
            setSelectedDateRange?.([formattedDate, formattedDate]);
          } else {
            const yearMonth = dayjs(lastRecord.date).format("YYYY-MM");
            setLocalDateRange([yearMonth, yearMonth]);
            setSelectedDateRange?.([yearMonth, yearMonth]);
          }
        } else {
          setLocalDate(formattedDate);
          setSelectedDate?.(formattedDate);
        }
      }
    }
  }, [
    dateRange,
    isLastChangePending,
    isLastRecordPending,
    lastChange,
    lastRecord,
    localDateRange,
    page,
    setSelectedDate,
    setSelectedDateRange,
    useDayRangePicker,
  ]);

  useEffect(() => {
    setLocalPeriod((current) =>
      clampPeriod(current, normalizedPeriodMin, safePeriodMax)
    );
  }, [normalizedPeriodMin, safePeriodMax]);

  const periodMarks = useMemo(() => {
    const marks = {
      [normalizedPeriodMin]: `${normalizedPeriodMin}`,
      [safePeriodMax]: `${safePeriodMax}`,
    };
    const middleMark = Math.round((normalizedPeriodMin + safePeriodMax) / 2);

    if (middleMark !== normalizedPeriodMin && middleMark !== safePeriodMax) {
      marks[middleMark] = `${middleMark}`;
    }

    return marks;
  }, [normalizedPeriodMin, safePeriodMax]);

  const getTagPlaceholder = (selectedItems, allItems) => {
    if (
      selectedItems?.length === allItems?.length - 1 ||
      selectedItems?.includes("all")
    ) {
      return "Todos";
    }

    if (selectedItems?.length === 1) {
      return "1 selecionado";
    }

    return `${selectedItems?.length} selecionados`;
  };

  const handleDateChange = (date) => {
    if (dateRange) {
      if (useDayRangePicker) {
        if (page === "flow_detections" && date?.[0] && date?.[1]) {
          const spanDays = date[1]
            .startOf("day")
            .diff(date[0].startOf("day"), "day");

          if (spanDays >= maxFlowRangeSpanDays) {
            setLocalDateRange([
              date[0].format("YYYY-MM-DD"),
              date[0].add(maxFlowRangeSpanDays - 1, "day").format("YYYY-MM-DD"),
            ]);
            return;
          }
        }

        setLocalDateRange(
          date
            ? [date[0].format("YYYY-MM-DD"), date[1].format("YYYY-MM-DD")]
            : null
        );
      } else {
        setLocalDateRange(
          date ? [date[0].format("YYYY-MM"), date[1].format("YYYY-MM")] : null
        );
      }
      setPickerDates(null);
      return;
    }

    setLocalDate(date ? date.format("YYYY-MM-DD") : null);
  };

  const handleCalendarChange = (dates) => {
    if (!dateRange) return;
    setPickerDates(dates);
    if (!useDayRangePicker || !dates) return;
    const [start, end] = dates;
    if (start && end && page !== "flow_detections") {
      setLocalDateRange([start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")]);
    }
    if (page === "flow_detections") {
      setPickingStart(start && !end ? start.format("YYYY-MM-DD") : null);
    }
  };

  const handlePickerOpenChange = (open) => {
    if (!dateRange) return;
    if (open) {
      // Reset any stale in-progress selection so the first click of a new
      // pick never pairs with a leftover bound from the previous range —
      // that's what was causing the picker to auto-close after one click.
      setPickerDates([null, null]);
    } else {
      setPickerDates(null);
      if (page === "flow_detections") {
        setPickingStart(null);
      }
    }
  };

  const disableRangeDate = useCallback(
    (currentDate) => {
      if (page !== "flow_detections" || !currentDate) return false;
      if (!pickingStart) return false;
      const startDate = dayjs(pickingStart);
      return (
        Math.abs(
          currentDate.startOf("day").diff(startDate.startOf("day"), "day")
        ) >= maxFlowRangeSpanDays
      );
    },
    [page, pickingStart, maxFlowRangeSpanDays]
  );

  const rangePickerValue = useMemo(
    () =>
      pickerDates ?? [
        localDateRange?.[0] ? dayjs(localDateRange[0]) : null,
        localDateRange?.[1] ? dayjs(localDateRange[1]) : null,
      ],
    [localDateRange, pickerDates]
  );

  const datePickerValue = useMemo(
    () => (localDate ? dayjs(localDate) : null),
    [localDate]
  );

  const handleApplyFilters = () => {
    const filterActions = {
      data: () =>
        dateRange
          ? localDateRange
            ? setSelectedDateRange?.([localDateRange[0], localDateRange[1]])
            : setSelectedDateRange?.(null)
          : setSelectedDate?.(localDate),
      periodo: () => setSelectedPeriod?.(localPeriod),
      zonas: () => setSelectedZones?.(localZones),
      colecoes: () => setSelectedCollections?.(localCollections),
      lojas: () => setSelectedStores?.(localStores),
      vendedores: () => setSelectedSellers?.(localSellers),
      eventos: () => setSelectedEvent?.(localEvent),
      dias_analise: () => {
        setSelectedAnalysisDays?.(localAnalysisDays);
        setSelectedBaseline?.(localBaseline);
      },
    };

    filters.forEach((filter) => filterActions[filter]?.());
  };

  return (
    <Card
      title={title}
      loading={loading}
      style={{
        width: "85%",
        maxWidth: "100%",
        backgroundColor: "var(--ouran-card-header-bg)",
        ...cardStyle,
      }}
      styles={{
        body: {
          padding: compactCardBodyPadding,
        },
      }}>
      <Row
        align="middle"
        justify="space-between"
        gutter={compactGutter}
        wrap
        className="ouran-filters-row"
        style={{ minHeight: compactRowMinHeight }}>
        {filters.includes("data") && (
          <Col style={filterColStyle} className="ouran-filter-col">
            <Text strong style={labelStyle}>
              {"Data:"}
              {page === "flow_detections" && dateRange && (
                <Text
                  type="secondary"
                  style={{ fontWeight: "normal", fontSize: 11, marginLeft: 6 }}>
                  {`(máx. ${maxFlowRangeSpanDays} dias)`}
                </Text>
              )}
            </Text>
            <Flex align="center" gap={8}>
              {dateRange ? (
                <DatePicker.RangePicker
                  size={compactControlSize}
                  format={useDayRangePicker ? "DD-MM-YYYY" : "YYYY/MM"}
                  picker={useDayRangePicker ? "date" : "month"}
                  style={{ width: datePickerWidth }}
                  placeholder={["Data inicial", "Data final"]}
                  value={rangePickerValue}
                  onChange={handleDateChange}
                  onCalendarChange={handleCalendarChange}
                  onOpenChange={handlePickerOpenChange}
                  disabledDate={disableRangeDate}
                />
              ) : (
                <DatePicker
                  size={compactControlSize}
                  style={{ width: selectWidth }}
                  picker={page === "collections_change" ? "month" : "date"}
                  placeholder={
                    page === "collections_change"
                      ? "Selecione um mês"
                      : "Selecione uma data"
                  }
                  value={datePickerValue}
                  onChange={handleDateChange}
                  format={
                    page === "collections_change" ? "YYYY/MM" : "DD-MM-YYYY"
                  }
                />
              )}
              {dateAddon}
            </Flex>
          </Col>
        )}

        {extraControls && (
          <Col style={filterColStyle} className="ouran-filter-col">
            {extraControls}
          </Col>
        )}

        {eventosHabilitados && (
          <Col style={filterColStyle} className="ouran-filter-col">
            <Text strong style={labelStyle}>
              Evento:
            </Text>
            <Select
              size={compactControlSize}
              style={{ width: "240px" }}
              popupMatchSelectWidth={false}
              placeholder="Todos"
              allowClear
              {...selectSearchProps}
              loading={isEventsFetching}
              notFoundContent="Nenhum evento no período"
              value={localEvent}
              onChange={handleEventChange}
              options={eventOptions}
            />
          </Col>
        )}

        {filters.includes("periodo") && (
          <Col style={filterColStyle} className="ouran-filter-col">
            <Text strong style={labelStyle}>
              Período (h):
            </Text>
            <Slider
              range
              value={localPeriod}
              onChange={setLocalPeriod}
              min={normalizedPeriodMin}
              max={safePeriodMax}
              marks={periodMarks}
              style={{ width: "200px" }}
              styles={{
                track: { backgroundColor: "black" },
              }}
            />
          </Col>
        )}

        {filters.includes("zonas") &&
          (() => {
            const realZones = getRealItems(zones);
            return (
              <Col style={filterColStyle} className="ouran-filter-col">
                <Text strong style={labelStyle}>
                  Zonas:
                </Text>
                <Select
                  mode="multiple"
                  size={compactControlSize}
                  style={{ width: selectWidth }}
                  placeholder="Selecione uma zona"
                  disabled={eventoAtivo}
                  allowClear
                  {...selectSearchProps}
                  maxTagPlaceholder={() => getTagPlaceholder(localZones, zones)}
                  value={getSelectValue(localZones, realZones)}
                  onChange={makeOnChange(setLocalZones, realZones)}
                  onDropdownVisibleChange={(open) => {
                    if (!open && localZones.length === 0)
                      setLocalZones(["all"]);
                  }}
                  dropdownRender={makeDropdownRender(
                    localZones,
                    setLocalZones,
                    realZones
                  )}
                  optionRender={makeOptionRender(localZones)}
                  maxTagCount={0}
                  options={realZones}
                />
              </Col>
            );
          })()}

        {filters.includes("colecoes") &&
          (() => {
            const realCollections = getRealItems(collections);
            return (
              <Col style={filterColStyle} className="ouran-filter-col">
                <Text strong style={labelStyle}>
                  Categoria:
                </Text>
                <Select
                  mode="multiple"
                  size={compactControlSize}
                  style={{ width: selectWidth }}
                  placeholder="Selecione uma categoria"
                  disabled={eventoAtivo}
                  allowClear
                  {...selectSearchProps}
                  maxTagPlaceholder={() =>
                    getTagPlaceholder(localCollections, collections)
                  }
                  value={getSelectValue(localCollections, realCollections)}
                  onChange={makeOnChange(setLocalCollections, realCollections)}
                  onDropdownVisibleChange={(open) => {
                    if (!open && localCollections.length === 0)
                      setLocalCollections(["all"]);
                  }}
                  dropdownRender={makeDropdownRender(
                    localCollections,
                    setLocalCollections,
                    realCollections
                  )}
                  optionRender={makeOptionRender(localCollections)}
                  maxTagCount={0}
                  options={realCollections}
                />
              </Col>
            );
          })()}

        {filters.includes("dias_analise") && (
          <Col style={filterColStyle} className="ouran-filter-col">
            <Text strong style={labelStyle}>
              Janela de comparação:
            </Text>
            <Flex align="center" gap={8} wrap="wrap">
              <Text>Comparar</Text>
              <Select
                size={compactControlSize}
                style={{ width: "190px" }}
                value={opcaoDoDepois}
                onChange={setLocalAnalysisDays}
                options={opcoesDoDepois}
              />
              <Text>com</Text>
              <Select
                size={compactControlSize}
                style={{ width: "180px" }}
                value={localBaseline}
                onChange={setLocalBaseline}
                options={OPCOES_DO_ANTES}
              />
            </Flex>
            <Text type="secondary" style={{ marginTop: 6, fontSize: 12 }}>
              {avisoDaJanela}
            </Text>
          </Col>
        )}

        {filters.includes("lojas") &&
          (() => {
            const realStores = getRealItems(stores);
            return (
              <Col style={filterColStyle} className="ouran-filter-col">
                <Text strong style={labelStyle}>
                  Loja:
                </Text>
                <Select
                  mode="multiple"
                  size={compactControlSize}
                  style={{ width: selectWidth }}
                  placeholder="Selecione uma loja"
                  allowClear
                  {...selectSearchProps}
                  maxTagPlaceholder={() =>
                    getTagPlaceholder(localStores, stores)
                  }
                  value={getSelectValue(localStores, realStores)}
                  onChange={makeOnChange(setLocalStores, realStores)}
                  onDropdownVisibleChange={(open) => {
                    if (!open && localStores.length === 0)
                      setLocalStores(["all"]);
                  }}
                  dropdownRender={makeDropdownRender(
                    localStores,
                    setLocalStores,
                    realStores
                  )}
                  optionRender={makeOptionRender(localStores)}
                  maxTagCount={0}
                  options={realStores}
                />
              </Col>
            );
          })()}

        {filters.includes("vendedores") &&
          (() => {
            const realSellers = getRealItems(sellers);
            return (
              <Col style={filterColStyle} className="ouran-filter-col">
                <Text strong style={labelStyle}>
                  Vendedor:
                </Text>
                <Select
                  mode="multiple"
                  size={compactControlSize}
                  style={{ width: selectWidth }}
                  placeholder="Selecione um vendedor"
                  disabled={eventoAtivo}
                  allowClear
                  {...selectSearchProps}
                  maxTagPlaceholder={() =>
                    getTagPlaceholder(localSellers, sellers)
                  }
                  value={getSelectValue(localSellers, realSellers)}
                  onChange={makeOnChange(setLocalSellers, realSellers)}
                  onDropdownVisibleChange={(open) => {
                    if (!open && localSellers.length === 0)
                      setLocalSellers(["all"]);
                  }}
                  dropdownRender={makeDropdownRender(
                    localSellers,
                    setLocalSellers,
                    realSellers
                  )}
                  optionRender={makeOptionRender(localSellers)}
                  maxTagCount={0}
                  options={realSellers}
                />
              </Col>
            );
          })()}

        <Col
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <Button
            size={compactControlSize}
            type="primary"
            onClick={handleApplyFilters}
            loading={isMainDataLoading}>
            Aplicar filtros
          </Button>
        </Col>
      </Row>
    </Card>
  );
}
